const DB_NAME = 'valenpos_offline_operations_v1';
const DB_VERSION = 1;
const STORE_NAME = 'operations';
const LEGACY_SALES_DB_NAME = 'valenpos_offline_sales_v1';
const LEGACY_SALES_STORE_NAME = 'sales';
const QUEUE_EVENT = 'valenpos:offline-operations-changed';
const LEGACY_QUEUE_EVENT = 'valenpos:offline-sales-changed';
const CHANNEL_NAME = 'valenpos-offline-operations';

export const OFFLINE_OPERATION_STATUS = Object.freeze({
  PENDING: 'pending',
  SYNCING: 'syncing',
  NEEDS_REVIEW: 'needs_review',
});

export const OFFLINE_OPERATION_TYPE = Object.freeze({
  CASH_OPEN: 'cash_open',
  CASH_RECONCILE: 'cash_reconcile',
  SALE: 'sale',
});

let readyPromise = null;
let syncPromise = null;
let broadcastChannel = null;

const requestResult = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('No se pudo acceder al almacenamiento local.'));
});

const transactionDone = (transaction) => new Promise((resolve, reject) => {
  transaction.oncomplete = () => resolve();
  transaction.onerror = () => reject(transaction.error || new Error('No se pudo guardar la operación local.'));
  transaction.onabort = () => reject(transaction.error || new Error('Se canceló el guardado de la operación local.'));
});

const openDatabase = (name, version, upgrade) => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    reject(new Error('Este dispositivo no permite guardar operaciones offline.'));
    return;
  }
  const request = version ? indexedDB.open(name, version) : indexedDB.open(name);
  request.onupgradeneeded = () => upgrade?.(request.result, request.transaction);
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('No se pudo abrir el almacenamiento offline.'));
});

const openOperationsDb = () => openDatabase(DB_NAME, DB_VERSION, (database) => {
  if (database.objectStoreNames.contains(STORE_NAME)) return;
  const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
  store.createIndex('createdAt', 'createdAt');
  store.createIndex('status', 'status');
  store.createIndex('type', 'type');
  store.createIndex('cashId', 'cashId');
});

const normalizeTimestamp = (value, fallback = Date.now()) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
};

export const normalizeOfflineOperation = (operation) => {
  const now = Date.now();
  const createdAt = normalizeTimestamp(operation?.createdAt, now);
  const id = String(operation?.id || '').trim();
  const type = String(operation?.type || '').trim().toLowerCase();
  if (!id || !type) throw new Error('La operación offline no tiene identificador o tipo.');

  return {
    schemaVersion: 1,
    ...operation,
    id,
    type,
    schemaVersion: Number(operation?.schemaVersion || 1),
    priority: Number.isFinite(Number(operation?.priority)) ? Number(operation.priority) : 100,
    attempts: Math.max(0, Number(operation?.attempts || 0)),
    lastError: String(operation?.lastError || ''),
    errorCode: String(operation?.errorCode || ''),
    status: Object.values(OFFLINE_OPERATION_STATUS).includes(operation?.status)
      ? operation.status
      : OFFLINE_OPERATION_STATUS.PENDING,
    createdAt,
    occurredAt: normalizeTimestamp(operation?.occurredAt, createdAt),
    updatedAt: normalizeTimestamp(operation?.updatedAt, now),
    dependsOn: Array.isArray(operation?.dependsOn)
      ? operation.dependsOn.map(String).filter(Boolean)
      : [],
  };
};

export const legacySaleToOperation = (record) => normalizeOfflineOperation({
  id: record.idempotencyKey,
  type: OFFLINE_OPERATION_TYPE.SALE,
  priority: 30,
  status: record.status,
  attempts: record.attempts,
  lastError: record.lastError || '',
  errorCode: record.errorCode || '',
  storeId: record.storeId || 'SUC001',
  cashId: record.cashId || record.saleDoc?.turnId || null,
  ownerUid: record.ownerUid || record.saleDoc?.sellerUid || null,
  createdAt: record.createdAt,
  occurredAt: record.createdAt,
  updatedAt: record.updatedAt,
  payload: {
    saleDoc: record.saleDoc,
    idempotencyKey: record.idempotencyKey,
    fingerprint: record.fingerprint,
    provisionalNumber: record.provisionalNumber,
  },
  migratedFrom: LEGACY_SALES_DB_NAME,
});

export const operationToOfflineSale = (operation) => ({
  idempotencyKey: operation?.payload?.idempotencyKey || operation?.id,
  fingerprint: operation?.payload?.fingerprint || '',
  saleDoc: operation?.payload?.saleDoc || null,
  storeId: operation?.storeId || 'SUC001',
  cashId: operation?.cashId || operation?.payload?.saleDoc?.turnId || null,
  ownerUid: operation?.ownerUid || operation?.payload?.saleDoc?.sellerUid || null,
  createdAt: operation?.createdAt,
  updatedAt: operation?.updatedAt,
  status: operation?.status,
  attempts: operation?.attempts || 0,
  lastError: operation?.lastError || '',
  errorCode: operation?.errorCode || '',
  provisionalNumber: operation?.payload?.provisionalNumber || '',
});

async function readLegacySales() {
  let createdEmptyDatabase = false;
  const database = await openDatabase(LEGACY_SALES_DB_NAME, null, () => {
    createdEmptyDatabase = true;
  });
  try {
    if (createdEmptyDatabase || !database.objectStoreNames.contains(LEGACY_SALES_STORE_NAME)) return [];
    const transaction = database.transaction(LEGACY_SALES_STORE_NAME, 'readonly');
    const done = transactionDone(transaction);
    const records = await requestResult(transaction.objectStore(LEGACY_SALES_STORE_NAME).getAll());
    await done;
    return records;
  } finally {
    database.close();
    if (createdEmptyDatabase) indexedDB.deleteDatabase(LEGACY_SALES_DB_NAME);
  }
}

async function removeMigratedLegacySales(ids) {
  if (!ids.length) return;
  const database = await openDatabase(LEGACY_SALES_DB_NAME);
  try {
    if (!database.objectStoreNames.contains(LEGACY_SALES_STORE_NAME)) return;
    const transaction = database.transaction(LEGACY_SALES_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(LEGACY_SALES_STORE_NAME);
    ids.forEach((id) => store.delete(id));
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

async function migrateLegacySales() {
  const records = await readLegacySales();
  if (!records.length) return;
  const database = await openOperationsDb();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    records.forEach((record) => store.put(legacySaleToOperation(record)));
    await transactionDone(transaction);
  } finally {
    database.close();
  }
  await removeMigratedLegacySales(records.map((record) => record.idempotencyKey).filter(Boolean));
}

async function recoverInterruptedOperations() {
  let database = await openOperationsDb();
  let records = [];
  try {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const done = transactionDone(transaction);
    records = await requestResult(transaction.objectStore(STORE_NAME).getAll());
    await done;
  } finally {
    database.close();
  }
  const interrupted = records.filter((record) => record.status === OFFLINE_OPERATION_STATUS.SYNCING);
  if (!interrupted.length) return;
  database = await openOperationsDb();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    interrupted.forEach((record) => store.put({
        ...record,
        status: OFFLINE_OPERATION_STATUS.PENDING,
        updatedAt: Date.now(),
        lastError: record.lastError || 'La sincronización anterior fue interrumpida.',
      }));
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

export function initializeOfflineOperations() {
  if (readyPromise) return readyPromise;
  readyPromise = (async () => {
    const database = await openOperationsDb();
    database.close();
    await migrateLegacySales();
    await recoverInterruptedOperations();
  })().catch((error) => {
    readyPromise = null;
    throw error;
  });
  return readyPromise;
}

const notifyLocalSubscribers = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(QUEUE_EVENT));
  window.dispatchEvent(new CustomEvent(LEGACY_QUEUE_EVENT));
};

const getBroadcastChannel = () => {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
    broadcastChannel.onmessage = notifyLocalSubscribers;
  }
  return broadcastChannel;
};

const emitQueueChanged = () => {
  notifyLocalSubscribers();
  getBroadcastChannel()?.postMessage({ type: 'changed', at: Date.now() });
};

export async function enqueueOfflineOperation(operation) {
  await initializeOfflineOperations();
  const record = normalizeOfflineOperation(operation);
  const database = await openOperationsDb();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(record);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
  emitQueueChanged();
  return record;
}

export async function listOfflineOperations() {
  await initializeOfflineOperations();
  const database = await openOperationsDb();
  try {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const done = transactionDone(transaction);
    const records = await requestResult(transaction.objectStore(STORE_NAME).getAll());
    await done;
    return records
      .map(normalizeOfflineOperation)
      .sort((left, right) => (
        Number(left.priority || 100) - Number(right.priority || 100)
        || Number(left.createdAt || 0) - Number(right.createdAt || 0)
        || String(left.id).localeCompare(String(right.id))
      ));
  } finally {
    database.close();
  }
}

export async function putOfflineOperation(operation) {
  return enqueueOfflineOperation(operation);
}

export async function addOfflineOperationDependency(operationId, dependencyId) {
  const records = await listOfflineOperations();
  const operation = records.find((record) => record.id === String(operationId));
  if (!operation) return null;
  const dependsOn = Array.from(new Set([...(operation.dependsOn || []), String(dependencyId)]));
  return putOfflineOperation({ ...operation, dependsOn, updatedAt: Date.now() });
}

export async function removeOfflineOperation(id) {
  await initializeOfflineOperations();
  const database = await openOperationsDb();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(String(id));
    await transactionDone(transaction);
  } finally {
    database.close();
  }
  emitQueueChanged();
}

export function subscribeOfflineOperations(callback) {
  if (typeof window === 'undefined') return () => {};
  getBroadcastChannel();
  window.addEventListener(QUEUE_EVENT, callback);
  return () => window.removeEventListener(QUEUE_EVENT, callback);
}

export const shouldRetryOfflineOperation = (error) => (
  !error?.status
  || error?.uncertain === true
  || ['SALE_NETWORK_UNCERTAIN', 'SALE_PROCESSING', 'CASH_NETWORK_ERROR'].includes(error?.code)
);

const emptySummary = () => ({ confirmed: 0, pending: 0, review: 0, skipped: 0, total: 0 });

async function runSync(processors, { onProgress, includeNeedsReview = false, shouldProcess } = {}) {
  const records = await listOfflineOperations();
  const summary = { ...emptySummary(), total: records.length };
  const completedIds = new Set();
  const queuedIds = new Set(records.map((record) => record.id));

  for (const record of records) {
    if (typeof shouldProcess === 'function' && !shouldProcess(record)) {
      summary.skipped += 1;
      continue;
    }
    if (record.status === OFFLINE_OPERATION_STATUS.NEEDS_REVIEW && !includeNeedsReview) {
      summary.review += 1;
      continue;
    }
    const processor = processors?.[record.type];
    if (typeof processor !== 'function') {
      summary.skipped += 1;
      continue;
    }
    const pendingDependency = record.dependsOn.some((id) => queuedIds.has(id) && !completedIds.has(id));
    if (pendingDependency) {
      summary.skipped += 1;
      continue;
    }

    const syncing = {
      ...record,
      status: OFFLINE_OPERATION_STATUS.SYNCING,
      attempts: Number(record.attempts || 0) + 1,
      updatedAt: Date.now(),
    };
    await putOfflineOperation(syncing);
    onProgress?.(syncing);
    try {
      const result = await processor(syncing);
      await removeOfflineOperation(syncing.id);
      completedIds.add(syncing.id);
      summary.confirmed += 1;
      onProgress?.({ ...syncing, status: 'confirmed', result });
    } catch (error) {
      const retry = shouldRetryOfflineOperation(error);
      const failed = {
        ...syncing,
        status: retry ? OFFLINE_OPERATION_STATUS.PENDING : OFFLINE_OPERATION_STATUS.NEEDS_REVIEW,
        lastError: error?.message || 'No se pudo sincronizar la operación.',
        errorCode: error?.code || 'OFFLINE_OPERATION_FAILED',
        updatedAt: Date.now(),
      };
      await putOfflineOperation(failed);
      if (retry) summary.pending += 1;
      else summary.review += 1;
      onProgress?.(failed);
      if (retry) break;
    }
  }
  return summary;
}

export function syncOfflineOperations(processors, options = {}) {
  if (syncPromise) return syncPromise;
  const execute = async () => {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      const result = await navigator.locks.request(
        'valenpos-offline-sync',
        { mode: 'exclusive', ifAvailable: true },
        (lock) => (lock ? runSync(processors, options) : null)
      );
      return result || { ...emptySummary(), locked: true };
    }
    return runSync(processors, options);
  };
  syncPromise = execute().finally(() => {
    syncPromise = null;
  });
  return syncPromise;
}
